import { useMemo } from 'react';

import { type GlobalShortcutsItem } from '../types';

export type GlobalShortcutsItemMap = {
	[key: string]: GlobalShortcutsItem;
};

export const useOrderedAppShortcuts = (
	globalShortcutsItemMap: GlobalShortcutsItemMap,
	customizationMenuConfig?: { menuId: string; visible: boolean }[],
): GlobalShortcutsItem[] => {
	return useMemo(() => {
		// return the original config if no customizationMenuConfig is provided
		if (!customizationMenuConfig || customizationMenuConfig.length === 0) {
			return Object.values(globalShortcutsItemMap);
		}

		// Create a map for low complexity lookups
		const configMap = new Map(customizationMenuConfig.map((config) => [config.menuId, config]));
		const result: GlobalShortcutsItem[] = [];
		const addedKeys = new Set<string>();

		// First pass: add items in the order specified by customizationMenuConfig
		customizationMenuConfig.forEach(({ menuId, visible }) => {
			if (visible && globalShortcutsItemMap[menuId]) {
				result.push(globalShortcutsItemMap[menuId]);
				addedKeys.add(menuId);
			}
		});

		// Second pass: add remaining items that aren't in customizationMenuConfig or aren't visible
		Object.entries(globalShortcutsItemMap).forEach(([key, value]) => {
			if (!addedKeys.has(key)) {
				const config = configMap.get(key);
				// Add if not in config (assume visible) or if in config but not visible
				if (!config) {
					result.push(value);
				}
			}
		});

		return result;
	}, [globalShortcutsItemMap, customizationMenuConfig]);
};
